import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/ui/Button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Dialog } from "primereact/dialog";
import { Spinner } from "react-bootstrap";

import {
    usePrayerSettings,
    useUpdatePrayerSettings,
    useTodaysPrayerTimes,
    useTestPrayerPause,
    usePrayerExceptions,
    useUpsertDeviceOverride,
    useDeleteDeviceOverride,
    useCitySearch,
    useGetDevices,
} from "../../../queries";
import Layout from "../../Layout/Layout";
import { useHasFeature } from "../../../hooks/useHasFeature";
import FeatureLocked from "../../SharedComponents/FeatureLocked";
import { formatUptime, fmtDuration } from "../../Devices/Component/Health/model";
import "../css/apps.css";

// 5 → 30 in 5-min steps. build 407: 5 minutes is offered here too - the Remote app has always offered it, and the two screens write the SAME setting
// (PUT /prayer/settings pause_duration_minutes), so a 5 set from the Remote must show as selected here instead of leaving no chip active.
export const PAUSE_DURATIONS = [5, 10, 15, 20, 25, 30].map((m) => ({ label: `${m} min`, value: m }));

/** The chips to show: the standard ones plus the current value when it is not one of them (the Remote also offers 45), in ascending order. */
export function durationOptions(current) {
    const base = PAUSE_DURATIONS.map((d) => d.value);
    const n = Number(current);
    const all = Number.isFinite(n) && n > 0 && !base.includes(n) ? [...base, n].sort((a, b) => a - b) : base;
    return all.map((m) => ({ label: `${m} min`, value: m }));
}

const CALC_METHODS = [
    { labelKey: "calc_umm_al_qura",   value: 4 },
    { labelKey: "calc_muslim_world_league", value: 3 },
    { labelKey: "calc_egyptian",       value: 5 },
    { labelKey: "calc_karachi",        value: 1 },
    { labelKey: "calc_isna",           value: 2 },
    { labelKey: "calc_uae",            value: 16 },
    { labelKey: "calc_kuwait",         value: 9 },
    { labelKey: "calc_qatar",          value: 10 },
    { labelKey: "calc_jordan",         value: 23 },
];

// Prayer key → i18n sub-key mapping. The underlying string value (fajr,
// dhuhr, etc.) MUST remain unchanged — it is sent to the API as
// skip_prayers[]. Only the displayed label is translated.
// Pause covers the five prayers ONLY (Sunrise is shown in the times table as information, never a pause option).
// Saved skip_prayers entries for anything else are left untouched; the backend ignores them.
const PRAYER_KEYS = ["fajr", "dhuhr", "asr", "maghrib", "isha"];

// Shared duration formatter (plural-aware sh.unit.* keys) - "47 دقيقة" / "47 minutes", never "47m".
function nextSecondsLabel(t, seconds) {
    if (seconds == null) return "—";
    if (seconds < 60) return fmtDuration(t, seconds);
    return formatUptime(t, Math.floor(seconds / 60), t("devices.group_just_now"));
}

// API prayer names are English keys (Fajr, Sunrise, ...); display them through prayer.prayer_<key>.
export function prayerLabel(t, name) {
    const key = String(name || "").toLowerCase();
    return ["fajr", "sunrise", "dhuhr", "asr", "maghrib", "isha"].includes(key)
        ? t(`prayer.prayer_${key}`)
        : name;
}

// Country code → IANA timezone for the major coffee/cafe markets popuppush
// runs in. Best-effort; falls back to whatever the user already had set if
// not in the map.
const COUNTRY_TZ = {
    sa: "Asia/Riyadh",
    ae: "Asia/Dubai",
    kw: "Asia/Kuwait",
    qa: "Asia/Qatar",
    bh: "Asia/Bahrain",
    om: "Asia/Muscat",
    jo: "Asia/Amman",
    eg: "Africa/Cairo",
    iq: "Asia/Baghdad",
    ly: "Africa/Tripoli",
    ma: "Africa/Casablanca",
    tn: "Africa/Tunis",
    dz: "Africa/Algiers",
    tr: "Europe/Istanbul",
    pk: "Asia/Karachi",
    in: "Asia/Kolkata",
    id: "Asia/Jakarta",
    my: "Asia/Kuala_Lumpur",
    gb: "Europe/London",
    us: "America/New_York",
    ca: "America/Toronto",
};

// ───────────────────────────────────────────────────────────────────
// City autocomplete component — reused by main settings + exception
// dialog. Setting a city writes lat/lng/name/country/timezone all at
// once so the user doesn't have to think about IANA timezones.
// ───────────────────────────────────────────────────────────────────
// Plain InputText + manually-rendered dropdown. We tried PrimeReact's
// AutoComplete first and the panel intermittently failed to open when
// suggestions arrived asynchronously from React Query — the component
// expects its `completeMethod` to populate suggestions synchronously,
// and stalls when they arrive a beat later. This controlled-render
// version is deterministic: the dropdown is visible iff
// (focused) ∧ (query.length ≥ 2) ∧ (matching suggestions or fetching).
function CityPicker({ value, onPick, placeholder }) {
    const { t } = useTranslation();
    const [query, setQuery] = useState(value || "");
    const [debounced, setDebounced] = useState("");
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    const { data: suggestions, isFetching } = useCitySearch(debounced, debounced.length >= 2);

    useEffect(() => { setQuery(value || ""); }, [value]);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(query), 350);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const onChangeText = (e) => {
        const v = e.target.value;
        setQuery(v);
        setOpen(true);
        onPick({
            location_name: v,
            location_lat: null,
            location_lng: null,
            country_name: null,
            timezone: null,
        });
    };

    const onSelect = (s) => {
        const city = s.city || (s.label || "").split(",")[0];
        setQuery(city);
        setOpen(false);
        onPick({
            location_name: city,
            country_name: s.country || null,
            location_lat: s.lat,
            location_lng: s.lng,
            timezone: COUNTRY_TZ[s.country_code] || null,
        });
    };

    const showPanel = open && query.length >= 2;

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <InputText
                value={query}
                onChange={onChangeText}
                onFocus={() => setOpen(true)}
                onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
                placeholder={placeholder || t("prayer.city_search_ph")}
                className="w-100"
                autoComplete="off"
            />
            {showPanel && (
                <div
                    style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        background: "white",
                        border: "1px solid #d9d9d9",
                        borderRadius: 6,
                        boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                        maxHeight: 280,
                        overflowY: "auto",
                        zIndex: 2000,
                    }}
                >
                    {isFetching && (!suggestions || suggestions.length === 0) ? (
                        <div className="px-3 py-2 text-muted small">{t("prayer.city_searching")}</div>
                    ) : !suggestions || suggestions.length === 0 ? (
                        <div className="px-3 py-2 text-muted small">{t("prayer.city_no_matches")}</div>
                    ) : (
                        suggestions.map((s, i) => (
                            <div
                                key={`${s.label}-${i}`}
                                role="button"
                                // onMouseDown beats input blur, so clicking a row
                                // doesn't dismiss the panel before onSelect runs.
                                onMouseDown={(e) => { e.preventDefault(); onSelect(s); }}
                                className="city-picker-row"
                                style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    borderBottom: i < suggestions.length - 1 ? "1px solid #f1f1f1" : "none",
                                    fontSize: 14,
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f7fa"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "white"; }}
                            >
                                <div style={{ fontWeight: 600 }}>{s.city || s.label.split(",")[0]}</div>
                                <div className="text-muted small">
                                    {s.country}
                                    {s.country_code ? ` (${s.country_code.toUpperCase()})` : ""}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

// ───────────────────────────────────────────────────────────────────
// Pause duration chip selector
// ───────────────────────────────────────────────────────────────────
function DurationChips({ value, onChange }) {
    const { t } = useTranslation();
    return (
        <div className="d-flex flex-wrap gap-2">
            {durationOptions(value).map((d) => {
                const active = d.value === value;
                return (
                    <button
                        key={d.value}
                        type="button"
                        title={fmtDuration(t, d.value * 60)}
                        aria-pressed={active}
                        onClick={() => onChange(d.value)}
                        className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-secondary"}`}
                        style={{ minWidth: 60 }}
                    >
                        {d.value}
                    </button>
                );
            })}
            <span className="align-self-center text-muted small ms-1">{t("prayer.minutes_label")}</span>
        </div>
    );
}

// ───────────────────────────────────────────────────────────────────
// Add / edit device exception dialog
// ───────────────────────────────────────────────────────────────────
function ExceptionDialog({ visible, onHide, exception, onSaved }) {
    const { t } = useTranslation();
    const { data: devicesData } = useGetDevices();
    const allDevices = devicesData?.data || devicesData || [];

    const upsert = useUpsertDeviceOverride();

    const editing = !!exception;
    // "Edit" pins to one device (override row is per-device). "Add" lets
    // you pick many at once so a venue with a Riyadh branch of 6 screens
    // doesn't have to repeat the dialog 6 times — we fan out the upsert
    // on submit.
    const [deviceIds, setDeviceIds] = useState(
        exception?.device_id ? [exception.device_id] : []
    );
    const [mode, setMode] = useState(
        exception?.override?.enabled === false ? "disabled" : "location"
    );
    const [form, setForm] = useState({
        location_lat: exception?.override?.location_lat ?? null,
        location_lng: exception?.override?.location_lng ?? null,
        location_name: exception?.override?.location_name ?? "",
        country_name: exception?.override?.country_name ?? null,
        timezone: exception?.override?.timezone ?? null,
        calculation_method: exception?.override?.calculation_method ?? null,
        pause_duration_minutes: exception?.override?.pause_duration_minutes ?? 15,
    });

    useEffect(() => {
        if (!visible) return;
        setDeviceIds(exception?.device_id ? [exception.device_id] : []);
        setMode(exception?.override?.enabled === false ? "disabled" : "location");
        setForm({
            location_lat: exception?.override?.location_lat ?? null,
            location_lng: exception?.override?.location_lng ?? null,
            location_name: exception?.override?.location_name ?? "",
            country_name: exception?.override?.country_name ?? null,
            timezone: exception?.override?.timezone ?? null,
            calculation_method: exception?.override?.calculation_method ?? null,
            pause_duration_minutes: exception?.override?.pause_duration_minutes ?? 15,
        });
    }, [visible, exception]);

    const deviceOptions = useMemo(() => {
        return (allDevices || []).map((d) => ({
            label: d.name || d.serial_name,
            value: d.id,
            sub: d.serial_name,
        }));
    }, [allDevices]);

    const calcMethodOptions = useMemo(() => {
        return [
            { label: t("prayer.calc_company_default"), value: null },
            ...CALC_METHODS.map((m) => ({ label: t(`prayer:${m.labelKey}`), value: m.value })),
        ];
    }, [t]);

    const canSave = deviceIds.length > 0 && (mode === "disabled" || !!form.location_name);

    const submit = async () => {
        if (!canSave) return;
        const payload =
            mode === "disabled"
                ? {
                      enabled: false,
                      // null out the rest so a previously-set location doesn't
                      // ghost-fire if the device is re-enabled later.
                      location_lat: null,
                      location_lng: null,
                      location_name: null,
                      country_name: null,
                      timezone: null,
                      calculation_method: null,
                      pause_duration_minutes: null,
                  }
                : {
                      enabled: true,
                      location_lat: form.location_lat,
                      location_lng: form.location_lng,
                      location_name: form.location_name,
                      country_name: form.country_name,
                      timezone: form.timezone,
                      calculation_method: form.calculation_method,
                      pause_duration_minutes: form.pause_duration_minutes,
                  };

        // Fan-out: one upsert per device. Sequential (not Promise.all) so
        // a late failure doesn't leave half-applied state under a 429.
        for (const deviceId of deviceIds) {
            await upsert.mutateAsync({ deviceId, payload });
        }
        onSaved?.();
        onHide();
    };

    const header = (
        <div>
            <i className="fas fa-tv me-2 text-secondary"></i>
            {editing ? t("prayer.exception_edit_title") : t("prayer.exception_add_title")}
        </div>
    );

    const saveLabel = upsert.isLoading
        ? t("prayer.saving")
        : deviceIds.length > 1
            ? t("prayer.save_n_devices", { count: deviceIds.length })
            : t("prayer.save_exception");

    const footer = (
        <div>
            <Button label={t("buttons.cancel")} className="p-button-text" onClick={onHide} />
            <Button
                label={saveLabel}
                icon="pi pi-save"
                onClick={submit}
                disabled={!canSave || upsert.isLoading}
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={header}
            footer={footer}
            style={{ width: "min(520px, 95vw)" }}
            modal
            className="p-fluid"
        >
            <div className="row g-3">
                <div className="col-12">
                    <label className="form-label small fw-semibold text-uppercase text-muted">
                        {editing ? t("prayer.device_singular") : t("prayer.device_plural")}
                    </label>
                    {editing ? (
                        <Dropdown
                            value={deviceIds[0] || null}
                            options={deviceOptions}
                            onChange={(e) => setDeviceIds(e.value ? [e.value] : [])}
                            placeholder={t("prayer.select_device")}
                            className="w-100"
                            disabled
                            filter
                        />
                    ) : (
                        <MultiSelect
                            value={deviceIds}
                            options={deviceOptions}
                            onChange={(e) => setDeviceIds(e.value || [])}
                            placeholder={t("prayer.select_devices")}
                            className="w-100"
                            filter
                            display="chip"
                            maxSelectedLabels={4}
                            selectedItemsLabel={t("prayer.n_devices_selected", { count: "{0}" })}
                        />
                    )}
                    {!editing && deviceIds.length > 1 && (
                        <div className="form-text">
                            {t("prayer.exception_will_apply", { count: deviceIds.length })}
                        </div>
                    )}
                </div>

                <div className="col-12">
                    <div className="d-flex flex-column gap-2">
                        <label className="form-check d-flex align-items-center mb-0">
                            <input
                                type="radio"
                                className="form-check-input me-2"
                                name="exception-mode"
                                checked={mode === "disabled"}
                                onChange={() => setMode("disabled")}
                            />
                            <span><i className="fas fa-ban text-danger me-1"></i> {t("prayer.exception_mode_disable")}</span>
                        </label>
                        <label className="form-check d-flex align-items-center mb-0">
                            <input
                                type="radio"
                                className="form-check-input me-2"
                                name="exception-mode"
                                checked={mode === "location"}
                                onChange={() => setMode("location")}
                            />
                            <span><i className="fas fa-map-marker-alt text-success me-1"></i> {t("prayer.exception_mode_location")}</span>
                        </label>
                    </div>
                </div>

                {mode === "location" && (
                    <>
                        <div className="col-12">
                            <label className="form-label small fw-semibold text-uppercase text-muted">{t("table.city")}</label>
                            <CityPicker
                                value={form.location_name}
                                onPick={(picked) =>
                                    setForm((f) => ({
                                        ...f,
                                        location_name: picked.location_name,
                                        country_name: picked.country_name || f.country_name,
                                        location_lat: picked.location_lat,
                                        location_lng: picked.location_lng,
                                        timezone: picked.timezone || f.timezone,
                                    }))
                                }
                                placeholder={t("prayer.city_search_exception_ph")}
                            />
                            {form.location_name && (
                                <div className="form-text">
                                    📍 {form.location_name}
                                    {form.country_name ? `, ${form.country_name}` : ""}
                                    {form.timezone ? ` · ${form.timezone}` : ""}
                                </div>
                            )}
                        </div>

                        <div className="col-12">
                            <label className="form-label small fw-semibold text-uppercase text-muted">{t("prayer.pause_duration")}</label>
                            <DurationChips
                                value={form.pause_duration_minutes}
                                onChange={(v) => setForm((f) => ({ ...f, pause_duration_minutes: v }))}
                            />
                        </div>

                        <div className="col-12">
                            <label className="form-label small fw-semibold text-uppercase text-muted">{t("prayer.calc_method_optional")}</label>
                            <Dropdown
                                value={form.calculation_method}
                                options={calcMethodOptions}
                                onChange={(e) => setForm((f) => ({ ...f, calculation_method: e.value }))}
                                className="w-100"
                            />
                        </div>
                    </>
                )}
            </div>
        </Dialog>
    );
}

// ───────────────────────────────────────────────────────────────────
// Main page
// ───────────────────────────────────────────────────────────────────
function PrayerCard() {
    const { t } = useTranslation();
    const allowed = useHasFeature("prayer_app");
    const { isLoading: loadingSettings, data: settings } = usePrayerSettings();
    const { isLoading: loadingTimes, data: timesData, refetch: refetchTimes, isFetching: fetchingTimes } = useTodaysPrayerTimes();
    const { data: exceptionsData, refetch: refetchExceptions } = usePrayerExceptions();
    const update = useUpdatePrayerSettings();
    const test = useTestPrayerPause();
    const deleteOverride = useDeleteDeviceOverride();

    const [form, setForm] = useState(null);
    const [showExceptionDialog, setShowExceptionDialog] = useState(false);
    const [editingException, setEditingException] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);

    useEffect(() => {
        if (settings) {
            setForm({
                enabled: Boolean(settings.enabled),
                location_lat: settings.location_lat ?? null,
                location_lng: settings.location_lng ?? null,
                location_name: settings.location_name ?? "",
                country_name: settings.country_name ?? "Saudi Arabia",
                timezone: settings.timezone ?? "Asia/Riyadh",
                calculation_method: settings.calculation_method ?? 4,
                pause_duration_minutes: settings.pause_duration_minutes ?? 20,
                resume_delay_minutes: settings.resume_delay_minutes ?? 5,
                show_logo: settings.show_logo ?? true,
                custom_message: settings.custom_message ?? "حان وقت الصلاة",
                custom_message_en: settings.custom_message_en ?? "Prayer Time",
                skip_prayers: settings.skip_prayers ?? [],
            });
        }
    }, [settings]);

    const calcMethodOptions = useMemo(() => {
        return CALC_METHODS.map((m) => ({ label: t(`prayer:${m.labelKey}`), value: m.value }));
    }, [t]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const toggleSkip = (key) => {
        setForm((f) => {
            const set = new Set(f.skip_prayers || []);
            if (set.has(key)) set.delete(key);
            else set.add(key);
            return { ...f, skip_prayers: Array.from(set) };
        });
    };

    const onSave = async () => {
        if (!form) return;
        await update.mutateAsync({
            ...form,
            location_lat: form.location_lat === "" ? null : form.location_lat,
            location_lng: form.location_lng === "" ? null : form.location_lng,
        });
        refetchTimes();
    };

    const onTest = () => test.mutate({ durationSeconds: 30 });

    const onPickCity = (picked) => {
        // CityPicker uses null to mean "clear" and undefined to mean
        // "leave unchanged." Use the property-presence check so a null
        // actually nulls (otherwise free-typing "Amman" would never
        // clear the previously-saved country_name="Jordan" — and the
        // form would render the confusing "Riyadh, Jordan" state).
        setForm((f) => {
            const next = { ...f };
            for (const key of [
                "location_name", "country_name", "location_lat", "location_lng", "timezone",
            ]) {
                if (Object.prototype.hasOwnProperty.call(picked, key)) {
                    next[key] = picked[key];
                }
            }
            return next;
        });
    };

    const onAddException = () => { setEditingException(null); setShowExceptionDialog(true); };
    const onEditException = (ex) => { setEditingException(ex); setShowExceptionDialog(true); };
    const onRemoveException = async (deviceId) => {
        if (!window.confirm(t("prayer.exception_remove_confirm"))) return;
        await deleteOverride.mutateAsync(deviceId);
        refetchExceptions();
    };

    const subNavbarProps = useMemo(() => ({
        title: t("prayer.page_title"),
        icon: <i className="fas fa-mosque text-secondary fs-3"></i>,
    }), [t]);

    // Feature gate AFTER all hooks — stable hook order across the companyDetails
    // async load that flips `allowed` false→true (an early return above the hooks
    // would change the hook count between renders → React crash).
    if (!allowed) {
        return (
            <Layout subNavbarProps={subNavbarProps}>
                <FeatureLocked feature="prayer_app" />
            </Layout>
        );
    }

    if (loadingSettings || !form) {
        return (
            <Layout subNavbarProps={subNavbarProps}>
                <div className="d-flex justify-content-center align-items-center my-5">
                    <Spinner variant="primary" as="span" animation="border" role="status" />
                </div>
            </Layout>
        );
    }

    const exceptions = exceptionsData?.exceptions || [];

    return (
        <Layout subNavbarProps={subNavbarProps}>
            <div className="row g-3">
                {/* ── Settings card ── */}
                <div className="col-12 col-lg-7">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                <h5 className="m-0">
                                    <i className="fas fa-mosque me-2 text-success"></i>
                                    {t("prayer.settings_title")}
                                </h5>
                                <div className="d-flex align-items-center">
                                    <span className="me-2 small text-muted">
                                        {form.enabled ? t("status.active") : t("status.inactive")}
                                    </span>
                                    <InputSwitch
                                        checked={form.enabled}
                                        onChange={(e) => setField("enabled", e.value)}
                                    />
                                </div>
                            </div>

                            <p className="text-muted small mb-3">
                                {t("prayer.settings_description")}
                            </p>

                            <div className="row g-3">
                                <div className="col-12">
                                    <label className="form-label small fw-semibold text-uppercase text-muted">
                                        <i className="fas fa-map-marker-alt me-1"></i> {t("prayer.default_location")}
                                    </label>
                                    <CityPicker
                                        value={form.location_name}
                                        onPick={onPickCity}
                                        placeholder={t("prayer.city_search_main_ph")}
                                    />
                                    {form.location_name && (
                                        <div className="form-text">
                                            📍 {form.location_name}
                                            {form.country_name ? `, ${form.country_name}` : ""}
                                            {form.timezone ? ` · ${form.timezone}` : ""}
                                        </div>
                                    )}
                                </div>

                                <div className="col-12">
                                    <label className="form-label small fw-semibold text-uppercase text-muted">
                                        <i className="fas fa-clock me-1"></i> {t("prayer.pause_duration")}
                                    </label>
                                    <DurationChips
                                        value={form.pause_duration_minutes}
                                        onChange={(v) => setField("pause_duration_minutes", v)}
                                    />
                                </div>

                                <div className="col-12 col-md-6">
                                    <label className="form-label small fw-semibold text-uppercase text-muted">
                                        {t("prayer.screen_message_ar")}
                                    </label>
                                    <InputText
                                        value={form.custom_message}
                                        onChange={(e) => setField("custom_message", e.target.value)}
                                        placeholder="حان وقت الصلاة"
                                        className="w-100"
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label small fw-semibold text-uppercase text-muted">
                                        {t("prayer.screen_message_en")}
                                    </label>
                                    <InputText
                                        value={form.custom_message_en}
                                        onChange={(e) => setField("custom_message_en", e.target.value)}
                                        placeholder="Prayer Time"
                                        className="w-100"
                                    />
                                </div>

                                <div className="col-12 d-flex align-items-center">
                                    <input
                                        type="checkbox"
                                        id="prayer-show-logo"
                                        className="form-check-input me-2"
                                        checked={form.show_logo}
                                        onChange={(e) => setField("show_logo", e.target.checked)}
                                    />
                                    <label htmlFor="prayer-show-logo" className="form-check-label">
                                        {t("prayer.show_logo_label")}
                                    </label>
                                </div>

                                <div className="col-12">
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                        onClick={() => setAdvancedOpen((v) => !v)}
                                    >
                                        <i className={`fas fa-chevron-${advancedOpen ? "down" : "right"} me-1`}></i>
                                        {t("prayer.advanced_toggle")}
                                    </button>
                                </div>

                                {advancedOpen && (
                                    <>
                                        <div className="col-12 col-md-6">
                                            <label className="form-label small fw-semibold text-uppercase text-muted">
                                                {t("prayer.calc_method")}
                                            </label>
                                            <Dropdown
                                                value={form.calculation_method}
                                                options={calcMethodOptions}
                                                onChange={(e) => setField("calculation_method", e.value)}
                                                className="w-100"
                                            />
                                        </div>

                                        <div className="col-12">
                                            <label className="form-label small fw-semibold text-uppercase text-muted">
                                                {t("prayer.skip_prayers")}
                                            </label>
                                            <div className="d-flex flex-wrap gap-3">
                                                {PRAYER_KEYS.map((k) => {
                                                    const skipped = (form.skip_prayers || []).includes(k);
                                                    return (
                                                        <label key={k} className="form-check d-flex align-items-center mb-0">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input me-1"
                                                                checked={skipped}
                                                                onChange={() => toggleSkip(k)}
                                                            />
                                                            {/* Display translated label; underlying value k is the API value */}
                                                            <span className="small">{t(`prayer:prayer_${k}`)}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                            <div className="form-text">{t("prayer.skip_hint")}</div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="d-flex justify-content-end pt-3">
                                <Button
                                    label={update.isLoading ? t("prayer.saving") : t("prayer.save_settings")}
                                    onClick={onSave}
                                    disabled={update.isLoading}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Today's prayer times ── */}
                <div className="col-12 col-lg-5">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                <h5 className="m-0">
                                    <i className="fas fa-calendar-day me-2 text-primary"></i>
                                    {t("prayer.todays_times_title")}
                                    {timesData?.location ? <small className="text-muted ms-2">— {timesData.location}</small> : null}
                                </h5>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => refetchTimes()}
                                    disabled={loadingTimes || fetchingTimes}
                                    title={t("buttons.refresh")}
                                >
                                    <i className={`fas fa-sync-alt ${fetchingTimes ? "fa-spin" : ""}`}></i>
                                </button>
                            </div>

                            {loadingTimes ? (
                                <div className="text-center my-4">
                                    <Spinner variant="primary" as="span" animation="border" role="status" />
                                </div>
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                        <table className="table table-sm align-middle mb-2">
                                            <tbody>
                                                {(timesData?.prayers || []).map((p) => (
                                                    <tr key={p.name} className={p.skipped || p.pausable === false ? "text-muted" : ""}>
                                                        <td>{prayerLabel(t, p.name)}</td>
                                                        <td className="text-end fw-bold">{p.time}</td>
                                                        <td style={{ width: 110 }} className="text-end">
                                                            {p.pausable === false ? (
                                                                <span className="text-muted small" data-testid="prayer-info-only">{t("prayer.info_only")}</span>
                                                            ) : p.skipped ? (
                                                                <span className="text-muted small">⏭ {t("prayer.prayer_skipped")}</span>
                                                            ) : p.passed ? (
                                                                <span className="text-muted small">✓</span>
                                                            ) : (
                                                                <span className="text-success small">✅</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {timesData?.next && (
                                        <div className="alert alert-info py-2 px-3 mb-3 small">
                                            ⏭ {t("prayer.next_prayer_label")}: <strong>{prayerLabel(t, timesData.next.name)}</strong> {t("prayer.next_prayer_in")}{" "}
                                            <strong>{nextSecondsLabel(t, timesData.next.seconds_remaining)}</strong> ({timesData.next.time})
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-end">
                                        <Button
                                            label={t("prayer.test_pause_btn")}
                                            className="p-button-outlined p-button-sm"
                                            onClick={onTest}
                                            disabled={test.isLoading}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Device Exceptions ── */}
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                <h5 className="m-0">
                                    <i className="fas fa-tv me-2 text-warning"></i>
                                    {t("prayer.exceptions_title")}
                                </h5>
                                <Button
                                    label={t("prayer.add_exception")}
                                    className="p-button-outlined p-button-sm"
                                    onClick={onAddException}
                                />
                            </div>

                            <p className="text-muted small">
                                {t("prayer.exceptions_description")}
                            </p>

                            {exceptions.length === 0 ? (
                                <div className="text-center py-3 text-muted small">
                                    {t("prayer.exceptions_empty")}
                                </div>
                            ) : (
                                <div className="list-group">
                                    {exceptions.map((ex) => {
                                        const o = ex.override;
                                        const disabled = o.enabled === false;
                                        return (
                                            <div key={ex.device_id} className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div>
                                                    <div className="fw-bold">
                                                        <i className="fas fa-tv me-2 text-secondary"></i>
                                                        {ex.device_name}
                                                    </div>
                                                    <div className="small text-muted mt-1">
                                                        {disabled ? (
                                                            <span className="text-danger">🚫 {t("prayer.exception_prayer_off")}</span>
                                                        ) : (
                                                            <>
                                                                📍 {o.location_name || "—"}
                                                                {o.country_name ? `, ${o.country_name}` : ""}
                                                                {o.pause_duration_minutes ? (
                                                                    <span className="ms-2">⏱ {formatUptime(t, o.pause_duration_minutes)}</span>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        icon="pi pi-pencil"
                                                        className="p-button-text p-button-sm"
                                                        onClick={() => onEditException(ex)}
                                                        tooltip={t("buttons.edit")}
                                                    />
                                                    <Button
                                                        icon="pi pi-trash"
                                                        className="p-button-text p-button-sm p-button-danger"
                                                        onClick={() => onRemoveException(ex.device_id)}
                                                        tooltip={t("buttons.delete")}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ExceptionDialog
                visible={showExceptionDialog}
                onHide={() => setShowExceptionDialog(false)}
                exception={editingException}
                onSaved={refetchExceptions}
            />
        </Layout>
    );
}

export default PrayerCard;
